/** Value used to represent unlimited resources (-1) */
export const UNLIMITED = -1;

/**
 * Checks if a limit value represents unlimited (-1).
 * Returns false for undefined (unknown state) - use nullish checks separately if needed.
 */
export const isUnlimited = (limit: number | undefined): boolean => {
  return limit === UNLIMITED;
};

/**
 * Checks if a resource limit has been reached.
 * Returns false if max is unlimited (-1) or undefined (unknown/loading state).
 * Returns true if max is 0 or any non-unlimited negative value (invalid config).
 */
export const isLimitReached = (current: number, max: number | undefined): boolean => {
  if (max === undefined || max === UNLIMITED) {
    return false;
  }
  // Treat 0 and invalid negative values (other than UNLIMITED) as "limit reached"
  if (max <= 0) {
    return true;
  }
  // Negative current values are treated as 0 (no usage)
  return Math.max(0, current) >= max;
};

/**
 * Calculates a percentage from current and max values, clamped between 0-100.
 * Returns 0 for:
 * - undefined inputs (unknown/loading state)
 * - unlimited max (UNLIMITED, -1)
 * - NaN values or non-positive max
 * - negative current values (treated as 0 usage)
 */
export const clampPercentage = (current: number | undefined, max: number | undefined): number => {
  if (current === undefined || max === undefined) {
    return 0;
  }
  // Explicitly handle unlimited - no percentage for unlimited resources
  if (isUnlimited(max)) {
    return 0;
  }
  if (isNaN(current) || isNaN(max) || max <= 0) {
    return 0;
  }
  // Treat negative current as 0
  const safeCurrent = Math.max(0, current);
  const percentage = (safeCurrent / max) * 100;
  return Math.min(100, Math.max(0, Math.round(percentage)));
};
